/**
* Shared rich Contact page for en/zh/ja/ko/vi/id locales.
*/
import { SITE } from "@/config/sites";
import { PageHero } from "@/components/PageHero";
import type { LocaleContactContent } from "@/data/locale-pages-content";
import type { DeepPageBlock } from "@/data/locale-page-deep";
import { LocalePageDeepBlock } from "@/components/LocalePageDeepBlock";

export function LocaleContactRich({ content: c, deep }: { content: LocaleContactContent; deep?: DeepPageBlock }) {
  const a = SITE.contact.address;
  return (
    <>
      <PageHero
        eyebrow={c.eyebrow}
        title={c.heroTitle}
        subtitle={c.heroSubtitle}
        primaryCta={{ label: c.ctaCall, href: `tel:${SITE.contact.phone}` }}
        secondaryCta={{ label: c.ctaLine, href: SITE.contact.lineUrl }}
      />

      <section className="mx-auto max-w-4xl px-4 pt-10">
        <div
          data-speakable
          className="rounded-xl border border-[var(--gold)]/40 bg-[var(--gold)]/[0.06] p-6"
        >
          <p className="text-xs font-semibold uppercase tracking-widest text-[var(--gold)]">
            {c.answerLabel}
          </p>
          <p className="mt-2 text-[15px] leading-relaxed text-foreground">{c.answer}</p>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-4 py-12 space-y-6 text-foreground/90">
        <h2 className="text-2xl font-bold text-foreground">{c.contactHeading}</h2>
        <p className="leading-relaxed">{c.intro}</p>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="rounded-xl border border-border bg-card p-6">
            <h3 className="font-semibold">{c.headOfficeLabel}</h3>
            <p className="mt-2 text-sm">{a.streetAddress}, {a.addressLocality}, {a.addressRegion} {a.postalCode}, {a.addressCountry}</p>
            <p className="mt-1 text-sm text-muted-foreground">{c.hoursLabel}: {SITE.contact.hoursSpec}</p>
          </div>
          <div className="rounded-xl border border-border bg-card p-6 space-y-2 text-sm">
            <p><span className="font-semibold">{c.lineLabel}:</span> <a className="underline" href={SITE.contact.lineUrl}>{SITE.contact.lineId}</a></p>
            <p><span className="font-semibold">{c.phoneLabel}:</span> <a className="underline" href={`tel:${SITE.contact.phone}`}>{SITE.contact.phone}</a></p>
            <p><span className="font-semibold">{c.emailLabel}:</span> <a className="underline" href={`mailto:${SITE.contact.email}`}>{SITE.contact.email}</a></p>
          </div>
        </div>
      </section>

      <section className="border-y border-border bg-muted/30 py-14">
        <div className="mx-auto max-w-6xl px-4">
          <h2 className="text-2xl font-bold text-foreground">{c.quickHelpHeading}</h2>
          <ul className="mt-6 grid gap-4 md:grid-cols-2">
            {c.quickHelp.map((q) => (
              <li key={q.title} className="rounded-xl border border-border bg-card p-5">
                <p className="font-semibold text-foreground">{q.title}</p>
                <p className="mt-2 text-sm text-foreground/80">{q.body}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-4 py-12">
        <h2 className="text-2xl font-bold text-foreground">{c.bookingHeading}</h2>
        <ul className="mt-4 space-y-2 text-sm">
          {c.bookingBullets.map((b) => (
            <li key={b} className="flex gap-2">
              <span className="mt-0.5 text-[var(--gold)]">•</span>
              <span>{b}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="mx-auto max-w-4xl px-4 pb-14">
        <h2 className="text-2xl font-bold text-foreground">{c.faqHeading}</h2>
        <div className="mt-6 space-y-3">
          {c.faq.map((f) => (
            <details key={f.q} className="group rounded-lg border border-border bg-card p-5">
              <summary className="cursor-pointer list-none font-semibold text-foreground marker:hidden">
                <span className="mr-2 text-[var(--gold)]">Q.</span>
                {f.q}
                <span className="float-right text-[var(--gold)] transition-transform group-open:rotate-45">＋</span>
              </summary>
              <p className="mt-3 border-t border-border pt-3 text-sm leading-relaxed text-foreground/85">
                <span className="mr-2 font-semibold text-[var(--gold)]">A.</span>
                {f.a}
              </p>
            </details>
          ))}
        </div>
      </section>

      {deep && <LocalePageDeepBlock block={deep} />}
    </>
  );
}
